import axios from 'axios';
import { InternalChromeConfig } from '@amzn/aac-chrome-internal-api';
import { DEVICE_TYPE, PAGE_LAYOUT_TYPE, VIEW_TYPE } from 'src/app/slice';
import { logger } from './logger';
import { CurrencySwitcherPublisher } from './metrics';
import { ATC_EXTERNAL_TOGGLE } from '../constants';

export const isStandardPageLayoutType = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  return (
    !!pageLayoutType &&
    (pageLayoutType === PAGE_LAYOUT_TYPE.STANDARD ||
      pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD)
  );
};

export const shouldRenderMenu = (
  viewType: VIEW_TYPE,
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  if (viewType === VIEW_TYPE.DESKTOP) {
    return (
      pageLayoutType !== PAGE_LAYOUT_TYPE.LIGHT &&
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS
    );
  } else {
    return pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS;
  }
};

export const currencyofViewSwitch = async (
  currentCurrency: string,
  newCurrency: string,
): Promise<void> => {
  const startTime = performance.now();
  const refTag = `ref=cov_${currentCurrency}_${newCurrency}`;
  const postURL = `/billing/currencyOfView/change?${refTag}`;

  const postData = {
    newCurrencyOfView: newCurrency,
    oldCurrencyOfView: currentCurrency,
  };

  try {
    const currencyOfViewChange = await axios(postURL, {
      method: 'POST',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      data: postData,
    });

    if (currencyOfViewChange) {
      CurrencySwitcherPublisher.publishCounterMonitor(
        'currecyOfViewSwitchSuccessCount',
        1,
      );

      window.location.reload();
    }
  } catch (error: any) {
    logger.error(
      'Error occurred while switching currency of view in AACChrome',
      error,
    );
    CurrencySwitcherPublisher.publishCounterMonitor(
      'currecyOfViewSwitchFailureCount',
      1,
    );
  } finally {
    CurrencySwitcherPublisher.publishTimerMonitor(
      'time',
      performance.now() - startTime,
    );
  }
};

export const showInternalView = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
  isInternalView = false,
): boolean => {
  const isExternalToggleOn =
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) === 'true';
  return (
    (pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD || isInternalView) &&
    !isExternalToggleOn
  );
};

export const getMetaTagContent = (metaTagName: string): string | null => {
  try {
    return JSON.parse(
      document
        .querySelector(`meta[name="${metaTagName}"]`)
        ?.getAttribute('content') ?? 'null',
    );
  } catch (e: any) {
    logger.error('fail to parse json for meta tag' + metaTagName, e);
    return null;
  }
};

export const getTrimInternalChromeConfig = ():
  | InternalChromeConfig
  | undefined => {
  try {
    return window.AACChrome.InternalAPI.InternalChromeConfigHandler.getInstance()
      .internalChromeConfig;
  } catch (e) {
    return {} as InternalChromeConfig;
  }
};

export const isMobileForTrim = (): boolean => {
  const internalChromeConfig = getTrimInternalChromeConfig();
  return (
    internalChromeConfig?.deviceType === DEVICE_TYPE.MOBILE &&
    internalChromeConfig?.mobileWebSupport === 'ENABLED' &&
    !internalChromeConfig?.forceDesktopSite
  );
};

/**
 * Converts a string to kebab-case format.
 *
 * @param {string} str - The input string to convert
 * @returns {string} The kebab-case formatted string
 *
 * @description
 * This function:
 * 1. Converts camelCase by adding hyphens between lowercase and uppercase letters
 * 2. Replaces spaces and underscores with hyphens
 * 3. Converts the entire string to lowercase
 */
export const toKebabCase = (str: string): string =>
  str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
